import type { ApiToggleFavoriteGamePort } from '~/handlers/ports/api-toggle-favorite-game.port';
import type {
  FavoriteGamesTogglePayload,
  FavoriteGamesToggleResponse,
} from '~/types/favorite-game';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';
import { getApostatudoRequestHeaders } from '~/utils/apostatudo/get-apostatudo-request-headers';

export class ApostatudoApiToggleFavoriteGameAdapter
  implements ApiToggleFavoriteGamePort
{
  private timeout = 10000; // 10 segundos
  private maxRetries = 2;

  async handle(
    payload: FavoriteGamesTogglePayload,
  ): Promise<FavoriteGamesToggleResponse | null> {
    let lastError: Error | null = null;

    // Tenta até maxRetries vezes
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        const response = await fetch(
          `${getApostatudoApiUrl()}/favorite-games`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              ...getApostatudoRequestHeaders(),
            },
            // ENG-497: só game_id — a identidade vem do Bearer.
            body: JSON.stringify({ game_id: payload.game_id }),
            signal: controller.signal,
          },
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          // Se for erro 4xx (erro do cliente), não tentar novamente
          if (response.status >= 400 && response.status < 500) {
            console.warn(
              `Erro ${response.status} ao alternar favorito - não será refeito`,
            );

            return null;
          }

          // Se for erro 5xx (erro do servidor), pode tentar novamente
          throw new Error(`HTTP ${response.status}`);
        }

        const data: FavoriteGamesToggleResponse = await response.json();

        return data;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (error instanceof Error && error.name === 'AbortError') {
          console.error(
            `Timeout ao alternar favorito - tentativa ${attempt + 1}/${this.maxRetries + 1}`,
          );
        } else {
          console.error(
            `Erro ao alternar favorito - tentativa ${attempt + 1}/${this.maxRetries + 1}:`,
            error,
          );
        }

        // Se não é a última tentativa, aguarda antes de tentar novamente
        if (attempt < this.maxRetries) {
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * (attempt + 1)),
          );
        }
      }
    }

    console.error(
      'Falha ao alternar favorito após todas as tentativas:',
      lastError,
    );

    return null;
  }
}
